import { useState, useEffect } from "react";
import { Text, Group, ActionIcon, Textarea, Button, Stack } from "@mantine/core";
import { IconThumbUp, IconThumbDown } from "@tabler/icons-react";
import { submitFeedback, getFeedbackForRequest } from "../api";
import { RailSection } from "./RailSection";

export default function FeedbackPanel({ requestId }) {
  const [score, setScore] = useState(null);
  const [text, setText] = useState("");
  const [showText, setShowText] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    getFeedbackForRequest(requestId).then((data) => {
      if (data) {
        setScore(data.score);
        setText(data.text || "");
        if (data.text) setShowText(true);
      }
      setLoaded(true);
    });
  }, [requestId]);

  const handleSubmit = async (newScore) => {
    setSaving(true);
    try {
      await submitFeedback({
        shuttleRequestId: requestId,
        score: newScore,
        text: text || null,
      });
      setScore(newScore);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComment = async () => {
    if (score == null) return;
    setSaving(true);
    try {
      await submitFeedback({
        shuttleRequestId: requestId,
        score,
        text: text || null,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  // Unselected verdicts stay subtle: two filled blocks weigh more than anything else in the rail
  // while saying nothing yet. Only the chosen one fills in, which is also what makes the current
  // verdict readable at a glance.
  const verdict = (value, Icon, color, label) => (
    <ActionIcon
      variant={score === value ? "filled" : "subtle"}
      color={score === value ? color : "gray"}
      size="md"
      aria-label={label}
      onClick={() => handleSubmit(value)}
      loading={saving}
    >
      <Icon size={16} />
    </ActionIcon>
  );

  return (
    <RailSection label="Feedback">
      <Stack gap="xs">
        <Group gap={4}>
          {verdict(1, IconThumbUp, "teal", "Good response")}
          {verdict(-1, IconThumbDown, "red", "Bad response")}
          {score != null && !showText && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              onClick={() => setShowText(true)}
            >
              Add comment
            </Button>
          )}
          {score == null && (
            <Text fz="xs" c="dimmed">
              Rate this response
            </Text>
          )}
        </Group>
        {showText && (
          <>
            <Textarea
              placeholder="Optional comment..."
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              minRows={2}
              size="xs"
            />
            <Button
              size="compact-xs"
              variant="default"
              onClick={handleSaveComment}
              loading={saving}
            >
              Save comment
            </Button>
          </>
        )}
      </Stack>
    </RailSection>
  );
}
